import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import IconSearch from "../../assets/svg/search-icon-grey.svg";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import toast from "react-hot-toast";
import config from "../../config";
import axiosInstant from "../../api";
import { useUser } from "../../hooks/useUser";
import { BrandCard } from "./components/BrandCard";
import { BrandIntelFooter } from "./components/BrandIntelFooter";
import { PageTitle } from "../../components/PageTitle";
import SortControls from "../../components/SortControls/SortControls";
import { MainContainer } from "../../components/ContentWrapper";
import { isAM, isInternal } from "../../utils";
import { ReactComponent as AddIcon } from "../../assets/svg/add-icon.svg";
import useDebounce from "../../hooks/useDebounce";
import { NoDataView } from "../../components/EmptyPanel/NoDataView";
import { IconProduct } from "../../utils/icons";
import { useCommonParams } from "../../hooks/useCommonParams";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CreateBrand from "./CreateBrand";
import { OverflowMenu, MENU_ICONS } from "../../components/MenuOptions/OverflowMenu";
import IdPill from "../../components/IdPill/IdPill";
import { LoadMoreBtn, DotLottleLoader, PageDivider } from "../../components/Loader";
import { usePagination } from "../../hooks/usePagination";

const PAGE_SIZE = 20;

const ACCOUNT_TYPE_OPTIONS = [
  { value: "all", label: "All account types" },
  { value: "brand", label: "Brands" },
  { value: "rmn", label: "RMNs" },
  { value: "agency", label: "Agencies" },
];

const PERIOD_LABELS = {
  today: "today",
  "7d": "last 7 days",
  "30d": "last 30 days",
  quarter: "last quarter",
  year: "last year",
  "5y": "last 5 years",
  "10y": "last 10 years",
  custom: "custom range",
};

export default function AllBrands() {
  const { role } = useUser();
  const navigate = useNavigate();
  const [showCreateBrandDialog, setShowCreateBrandDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const {
    searchParams,
    setSearchParams,
    page,
    setPage,
    sortType,
    setSortType,
    getSortParam,
    searchText,
    setSearchText,
  } = useCommonParams({ defaultSortKey: "created_at", sortKeyMap: { name: "Brand.name~i" } });
  const [localSearchInput, setLocalSearchInput] = useState(searchText);
  // Account type (API ?account_type, read off brands.plan).
  const [accountType, setAccountType] = useState(searchParams.get("account_type") || "");
  const debouncedSearchTerm = useDebounce(localSearchInput, 500);

  const onSearchChange = (e) => {
    setLocalSearchInput(e.target.value);
  };

  const handleSortChange = (newValue) => {
    setSortType({ ...sortType, key: newValue || "" });
  };

  const handleAccountTypeChange = (value) => {
    setAccountType(value === "all" ? "" : value);
  };

  const handleSortDirectionChange = (isAscending) => {
    setSortType({ ...sortType, isAscending });
  };

  const clearCreatedFilter = () => {
    setSearchParams((prev) => {
      prev.delete("period");
      prev.delete("from");
      prev.delete("to");
      return prev;
    });
  };

  useEffect(() => {
    setSearchText(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSearchParams((prev) => {
      prev.delete("rmn");
      if (accountType) prev.set("account_type", accountType);
      else prev.delete("account_type");
      return prev;
    });
  }, [accountType]);

  const sortParam = getSortParam();
  const periodFilter = searchParams.get("period") || "";
  const fromFilter = searchParams.get("from") || "";
  const toFilter = searchParams.get("to") || "";

  const fetchFn = useCallback(
    async (pageNum) => {
      try {
        const params = new URLSearchParams();
        params.set("page", String(pageNum));
        params.set("limit", String(PAGE_SIZE));
        params.set("sort", sortParam);
        params.set("counts", "true");
        // Consumer Voice / Brand Intelligence / PDP summary on each card
        // (internal callers only — the API ignores it otherwise).
        params.set("intel", "true");
        if (searchText) params.set("search", searchText);
        if (accountType) params.set("account_type", accountType);
        if (periodFilter) params.set("period", periodFilter);
        if (fromFilter) params.set("from", fromFilter);
        if (toFilter) params.set("to", toFilter);
        const url = `${config.API_URL}/brands?${params.toString()}`;
        const response = await axiosInstant.get(url);
        const { rows, count } = response.data;
        return { rows: rows ?? [], count: count ?? 0, error: null };
      } catch (e) {
        const msg = e?.response?.data?.error ?? "Failed to load brands";
        toast.error(msg);
        return { rows: [], count: 0, error: msg };
      }
    },
    [searchText, accountType, sortParam, periodFilter, fromFilter, toFilter],
  );

  const {
    items: brands,
    totalCount: count,
    loading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    error,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["page", "view"],
    },
  });

  // Cards carry pdp_report_running / cv_running / prep_pack_running; reload
  // while any brand on the page is analyzing so buttons re-enable and Last
  // Report fills in.
  const anyIntelRunning = brands.some((b) => b.pdp_report_running || b.cv_running || b.aip_running || b.prep_pack_running);
  useEffect(() => {
    if (!anyIntelRunning) return undefined;
    const t = setInterval(() => initLoad(page), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyIntelRunning]);

  const deleteBrand = (brand_id) => {
    setBrandToDelete(brand_id);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteBrand = () => {
    setShowDeleteConfirmation(false);
    axiosInstant
      .delete(config.API_URL + "/admin/brands/" + brandToDelete)
      .then(() => {
        toast.success("Brand deleted successfully!");
        initLoad(page);
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  };

  return (
    <MainContainer>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitle title="Brands" />

        {/* Sales creates brands for the prospects they sign up. */}
        {(isAM(role) || role === "SALES_TEAM") && (
          <button
            className="flex items-center gap-1 bg-accent text-base text-white py-3 px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline"
            onClick={() => setShowCreateBrandDialog(true)}
          >
            <AddIcon className="mr-1" />
            New Brand
          </button>
        )}
      </div>
      <div className="pt-8 pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="w-full sm:w-auto relative">
            <img
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
              src={IconSearch}
              alt=""
            />
            <input
              type="text"
              className="w-full sm:w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
              id="search-brands"
              placeholder="Search Brands"
              value={localSearchInput}
              onChange={(e) => onSearchChange(e)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <CustomSelect
              labelId="account-type"
              name="accountType"
              value={accountType || "all"}
              onChange={handleAccountTypeChange}
              options={ACCOUNT_TYPE_OPTIONS}
              width={190}
            />
            <SortControls
              showSelect
              sortKey={sortType.key}
              isAscending={sortType.isAscending}
              onSortKeyChange={handleSortChange}
              onDirectionChange={handleSortDirectionChange}
              options={[
                { value: "created_at", label: "Created Date" },
                { value: "name", label: "Name" },
              ]}
            />
          </div>
        </div>
      </div>

      {(periodFilter || fromFilter) && (
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-2 bg-tint text-accent-text text-[12.5px] font-semibold px-3 py-1.5 rounded-full">
            New brands{PERIOD_LABELS[periodFilter] ? ` · ${PERIOD_LABELS[periodFilter]}` : ""}
            <button
              type="button"
              onClick={clearCreatedFilter}
              className="text-accent-text hover:text-text-1 cursor-pointer leading-none text-[15px]"
              title="Clear filter"
            >
              ×
            </button>
          </span>
        </div>
      )}

      <PageDivider
        count={count}
        label="Brands"
        className="mt-5"
        canLoadPrevious={canLoadPrevious}
        hasMore={hasMore}
        onLoadPrevious={loadPrevious}
      />

      <div className="mt-5 mb-4">
        {loading && brands.length === 0 && <DotLottleLoader fill={false} />}

        {!loading && brands.length === 0 && !error && (
          <NoDataView
            text="No brands found"
            subText="Your search does not match any existing brands. "
            Icon={IconProduct}
            iconProps={{ shape: "square" }}
          />
        )}

        {brands.length > 0 && (
          <>
            {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
            {loadingPrevious && (
              <div className="flex justify-center py-2">
                <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
              </div>
            )}
            <div
              className="my-8 grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
            >
              {brands.map((brand) => {
                return (
                  <BrandCard
                    key={brand.id}
                    {...brand}
                    onClick={() => navigate(`/brands/${brand.id}/details`)}
                    footer={
                      isInternal(role) ? (
                        <BrandIntelFooter brand={brand} onChanged={() => initLoad(page)} />
                      ) : undefined
                    }
                  >
                    <BrandCardControls id={brand.id} deleteBrand={deleteBrand} />
                  </BrandCard>
                );
              })}
            </div>
          </>
        )}
      </div>

      {brands.length > 0 && hasMore && (
        <LoadMoreBtn onClick={loadMore} loading={loading} />
      )}

      <ConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDeleteBrand}
        title="Delete Brand"
        description="Are you sure you want to delete this brand? This action is irreversible."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
      {showCreateBrandDialog && (
        <CreateBrand onClose={() => setShowCreateBrandDialog(false)} onSuccess={(brand) => {
          initLoad(1);
          setShowCreateBrandDialog(false);
        }} />
      )}
    </MainContainer>
  );
}


const BrandCardControls = ({ id, deleteBrand }) => {
  const { role } = useUser();
  const navigate = useNavigate();

  const groups = [
    {
      label: "Manage",
      items: [
        {
          key: "view",
          label: "View Details",
          icon: MENU_ICONS.view,
          onClick: () => navigate(`/brands/${id}/details`),
        },
      ],
    },
    {
      label: "",
      items: isAM(role)
        ? [
            {
              key: "delete",
              label: "Delete",
              icon: MENU_ICONS.trash,
              danger: true,
              onClick: () => deleteBrand(id),
            },
          ]
        : [],
    },
  ];

  return (
    <div className="flex items-center gap-[6px]">
      {isInternal(role) && <IdPill value={id} />}

      {(isAM(role) || role === "SALES_TEAM") && (
        <div onClick={(e) => e.stopPropagation()}>
          <OverflowMenu groups={groups} ariaLabel="Brand actions" width={230} />
        </div>
      )}
    </div>
  );
};
